import { getBlocklyDataProvider, type BlocklyDataProvider, type BlocklyDropdownOption } from './provider.ts';

export type DynamicDropdownRawOption = string | BlocklyDropdownOption;
export type DynamicDropdownOptions = DynamicDropdownRawOption[];
export type DynamicDropdownSource =
  | keyof BlocklyDataProvider
  | ((provider: BlocklyDataProvider) => DynamicDropdownOptions);

export type DynamicDropdownToken = {
  readonly __dynamicDropdownToken: true;
  readonly id: string;
  toString(): string;
};

type DynamicDropdownDefinition = {
  source: DynamicDropdownSource;
  staticPrefix: string[];
};

type RegisteredDropdownField = {
  dynamicId?: string;
  staticOptions?: BlocklyDropdownOption[];
  mapLabelsWithInvisibleSpaces: boolean;
};

export type DropdownOptionsResolverOptions = {
  mapLabelsWithInvisibleSpaces?: boolean;
};

const DYNAMIC_DROPDOWN_PREFIX = '@dyn:';
const INVISIBLE_SPACE = '​ ';
const definitions = new Map<string, DynamicDropdownDefinition>();
const registeredFields = new Map<string, RegisteredDropdownField>();
let nextDynamicDropdownId = 0;

export function dyn(source: DynamicDropdownSource, staticPrefix: string[] = []): DynamicDropdownToken {
  const id = `d${++nextDynamicDropdownId}`;
  definitions.set(id, { source, staticPrefix: [...staticPrefix] });
  return {
    __dynamicDropdownToken: true,
    id,
    toString: () => `${DYNAMIC_DROPDOWN_PREFIX}${id}`
  };
}

export function isDynamicDropdownToken(value: unknown): value is DynamicDropdownToken {
  return typeof value === 'object'
    && value !== null
    && (value as DynamicDropdownToken).__dynamicDropdownToken === true;
}

export function isDynamicDropdownMarker(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith(DYNAMIC_DROPDOWN_PREFIX);
}

export function getDynamicDropdownId(marker: string): string {
  return marker.slice(DYNAMIC_DROPDOWN_PREFIX.length);
}

export function registerDynamicDropdownField(
  blockType: string,
  fieldName: string,
  dynamicId: string,
  options: DropdownOptionsResolverOptions = {}
): void {
  registeredFields.set(getFieldKey(blockType, fieldName), {
    dynamicId,
    mapLabelsWithInvisibleSpaces: options.mapLabelsWithInvisibleSpaces ?? false
  });
}

export function registerStaticDropdownField(
  blockType: string,
  fieldName: string,
  options: BlocklyDropdownOption[]
): void {
  registeredFields.set(getFieldKey(blockType, fieldName), {
    staticOptions: options,
    mapLabelsWithInvisibleSpaces: false
  });
}

export function createDynamicDropdownGenerator(
  blockType: string,
  fieldName: string,
  dynamicId: string,
  options: DropdownOptionsResolverOptions = {}
): () => BlocklyDropdownOption[] {
  registerDynamicDropdownField(blockType, fieldName, dynamicId, options);
  return () => resolveDynamicDropdownOptions(dynamicId, options);
}

export function getDropdownOptionsFor(blockType: string, fieldName: string): BlocklyDropdownOption[] {
  const registered = registeredFields.get(getFieldKey(blockType, fieldName));
  if (!registered) {
    return [];
  }
  if (registered.staticOptions) {
    return registered.staticOptions;
  }
  if (!registered.dynamicId) {
    return [];
  }
  return resolveDynamicDropdownOptions(registered.dynamicId, {
    mapLabelsWithInvisibleSpaces: registered.mapLabelsWithInvisibleSpaces
  });
}

/** true, wenn die Optionen des Feldes zur Laufzeit aus dem BlocklyDataProvider (Bot-Daten) kommen. */
export function isDynamicDropdownField(blockType: string, fieldName: string): boolean {
  return registeredFields.get(getFieldKey(blockType, fieldName))?.dynamicId !== undefined;
}

export function getDropdownAllowedValuesFor(blockType: string, fieldName: string): string[] {
  return getDropdownOptionsFor(blockType, fieldName).map(([, value]) => value);
}

export function resolveDynamicDropdownOptions(
  dynamicId: string,
  options: DropdownOptionsResolverOptions = {}
): BlocklyDropdownOption[] {
  return resolveDynamicDropdownOptionsForProvider(dynamicId, getBlocklyDataProvider(), options);
}

export function resolveDynamicDropdownOptionsForProvider(
  dynamicId: string,
  provider: BlocklyDataProvider,
  options: DropdownOptionsResolverOptions = {}
): BlocklyDropdownOption[] {
  const definition = definitions.get(dynamicId);
  if (!definition) {
    return [['???', '???']];
  }

  const dynamicOptions = readProviderOptions(definition.source, provider);
  const resolved = [...definition.staticPrefix, ...dynamicOptions].map(option =>
    normalizeDropdownOption(option, options)
  );

  return resolved.length > 0 ? resolved : [['???', '???']];
}

export function parseStaticDropdownOptions(
  rawOptions: string,
  options: DropdownOptionsResolverOptions = {}
): BlocklyDropdownOption[] {
  return rawOptions
    .split(',')
    .map(item => normalizeDropdownOption(item, options));
}

function readProviderOptions(source: DynamicDropdownSource, provider: BlocklyDataProvider): DynamicDropdownOptions {
  let result: unknown;
  if (typeof source === 'function') {
    result = source(provider);
  } else {
    const providerMember = provider[source];
    result = typeof providerMember === 'function'
      ? (providerMember as () => unknown).call(provider)
      : [];
  }

  return Array.isArray(result) ? result as DynamicDropdownOptions : [];
}

function normalizeDropdownOption(
  option: DynamicDropdownRawOption,
  options: DropdownOptionsResolverOptions
): BlocklyDropdownOption {
  if (Array.isArray(option)) {
    return [normalizeLabel(option[0], options), option[1]];
  }

  const parts = option.split('|');
  if (parts.length === 2) {
    return [normalizeLabel(parts[0] ?? '', options), parts[1] ?? ''];
  }

  return [normalizeLabel(option, options), option];
}

function normalizeLabel(label: string, options: DropdownOptionsResolverOptions): string {
  return options.mapLabelsWithInvisibleSpaces ? label.split(' ').join(INVISIBLE_SPACE) : label;
}

function getFieldKey(blockType: string, fieldName: string): string {
  return `${blockType}:${fieldName}`;
}
